<template>
  <b-modal ref="approvalModal" :title="modalTitle" size="xl" hide-footer>
    <!-- Lines breakdown (BR-012: Approver phải thấy đầy đủ lines + budget) -->
    <div v-if="lines && lines.length > 0" class="mb-4">
      <h6 class="mb-2">Chi tiết khoản mục phí
        <b-badge variant="info" class="ml-1">{{ lines.length }} dòng</b-badge>
        <b-badge variant="success" class="ml-1">Tổng: {{ convertPrice(totalAmount) }} đ</b-badge>
      </h6>
      <div class="table-responsive">
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th style="width:60px">Dòng</th>
              <th>Khoản mục phí</th>
              <th>Cửa hàng</th>
              <th class="text-right">NS tháng (đã duyệt)</th>
              <th class="text-right">Số tiền (VNĐ)</th>
              <th class="text-center">Số tháng SD</th>
              <th class="text-right">NS còn lại sau PR</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in lines" :key="line.id || line.lineNumber">
              <td class="text-center">{{ line.lineNumber }}</td>
              <td>{{ line.costItemName || (line.costItem && line.costItem.name) || line.costItemId }}</td>
              <td>{{ line.storeName || (line.store && line.store.name) || 'Cấp phòng ban' }}</td>
              <td class="text-right">
                <span v-if="line.budgetAllocatedAmount != null" class="text-info">{{ convertPrice(line.budgetAllocatedAmount) }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-right"><strong>{{ convertPrice(line.requestedAmount) }}</strong></td>
              <td class="text-center">{{ line.budgetUsagePeriod || 1 }} tháng</td>
              <td class="text-right">
                <span
                  v-if="line.budgetRemainingAfterPR != null"
                  :class="line.budgetRemainingAfterPR < 0 ? 'text-danger' : 'text-success'"
                >{{ convertPrice(line.budgetRemainingAfterPR) }}</span>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-weight-bold">
              <td colspan="4" class="text-right">Tổng cộng:</td>
              <td class="text-right text-primary">{{ convertPrice(totalAmount) }}</td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <hr />
    </div>

    <!-- Approval chain -->
    <h6 class="mb-2">Quy trình duyệt</h6>
    <b-table
      v-if="filteredApprovers && filteredApprovers.length"
      :items="filteredApprovers"
      :fields="approverFields"
      bordered
      hover
      small
    >
      <template #cell(approverBy)="data">
        {{ data.item.approverByName }}
      </template>
      <template #cell(status)="data">
        <b-badge :variant="approverStatusVariant(data.value)">
          {{ approverStatusName(data.value) }}
        </b-badge>
      </template>
      <template #cell(updatedAt)="data">
        {{
          data.item.status !== APPROVER_STATUS.PENDING
            ? formatDateTime(data.value)
            : '--'
        }}
      </template>
    </b-table>

    <p v-else class="text-muted mb-0">Chưa có quy trình duyệt.</p>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import { APPROVER_STATUS, APPROVER_STATUS_BADGE_VARIANT } from '@/utils/enum';
import { APPROVER_STATUS_NAME } from '@/utils/enum-name';
import { handleError, convertPrice } from '@/utils/common';

export default {
  name: 'PurchaseRequestApprovalModal',
  props: {
    requestId: {
      type: [String, Number],
      default: null,
    },
    lines: {
      type: Array,
      default: () => [],
    },
    approvers: {
      type: Array,
      default: () => [],
    },
    modalTitle: {
      type: String,
      default: 'Quy trình duyệt',
    },
  },
  data() {
    return {
      APPROVER_STATUS,
      internalApprovers: [],
      loadedRequestId: null,
      selectedStep: null,
      isLoading: false,
    };
  },
  computed: {
    totalAmount() {
      return (this.lines || []).reduce((s, l) => s + (Number(l.requestedAmount) || 0), 0);
    },
    effectiveApprovers() {
      if (this.internalApprovers && this.internalApprovers.length) {
        return this.internalApprovers;
      }
      return this.approvers || [];
    },
    filteredApprovers() {
      if (!this.selectedStep) return this.effectiveApprovers;
      const stepNum = Number(this.selectedStep);
      return (this.effectiveApprovers || []).filter(
        (a) => Number(a.step) === stepNum,
      );
    },
    approverFields() {
      return [
        {
          key: 'step',
          label: 'Bước',
          thStyle: { width: '8%' },
          class: 'text-center',
        },
        {
          key: 'approverBy',
          label: 'Người duyệt',
          thStyle: { width: '25%' },
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { width: '15%' },
          class: 'text-center',
        },
        { key: 'note', label: 'Ghi chú' },
        {
          key: 'updatedAt',
          label: 'Thời gian',
          thStyle: { width: '20%' },
          class: 'text-center',
        },
      ];
    },
  },
  methods: {
    convertPrice,
    open({ step = null } = {}) {
      this.selectedStep = step;
      this.ensureApproversLoaded().finally(() => {
        this.$refs.approvalModal.show();
      });
    },
    approverStatusVariant(val) {
      return APPROVER_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    approverStatusName(val) {
      return APPROVER_STATUS_NAME[val] || val;
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '';
    },
    async ensureApproversLoaded() {
      if (
        this.internalApprovers &&
        this.internalApprovers.length &&
        this.loadedRequestId === this.requestId
      )
        return;
      if (this.approvers && this.approvers.length) return;
      if (!this.requestId) return;

      this.isLoading = true;
      try {
        const { data } = await ApiService.get(
          `v2/purchase-requests/${this.requestId}`,
        );
        if (data.status === 200 && data.data) {
          this.internalApprovers = data.data.approvers || [];
          this.loadedRequestId = this.requestId;
        }
      } catch (err) {
        handleError(err);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

